import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { Seo, AppButton, FlexibleSelectDropdown } from '@Components/Control';
import { AdminNavWrapper } from '@Components/Navigation';
import {
  initiateFetchCidAnalytics,
  initiateFetchCidAnalyticsFilterOptions,
  initiateFetchYtAudits,
  clearCidAnalytics,
} from '@Actions';
import { StyledPanel } from './StyledCidAnalyticsDashboardPage';
import {
  TABS,
  YEAR_OPTIONS,
  MONTH_OPTIONS,
  TabBar,
} from './CidAnalyticsShared';
import CidAnalyticsOverviewTab from './CidAnalyticsOverviewTab';
import CidAnalyticsWeeklyTab from './CidAnalyticsWeeklyTab';
import CidAnalyticsPerformanceTab from './CidAnalyticsPerformanceTab';
import CidAnalyticsEstimatedTab from './CidAnalyticsEstimatedTab';
import CidAnalyticsYtAuditsTab from './CidAnalyticsYtAuditsTab';

const AdminCidAnalyticsDashboardPage = ({
  small,
  location,
  loading,
  fetchAnalytics,
  fetchYtAudits,
  clearAnalyticsData,
  fetchFilterOptions,
  filterOptions = null,
  overview = null,
  creatorGrowth = null,
  revenueByMonth = null,
  revenueRanks = null,
  videoStats = null,
  revenuePerCreator = null,
  weeklyGrowth = null,
  viewsAnalytics = null,
  claimsPerformance = null,
  estimatedRevenue = null,
  ytAudits = null,
}) => {
  const {
    theme: { page, button },
  } = useAppTheme();

  const [activeTab, setActiveTab] = React.useState(TABS[0]);

  // Pending (UI) filter state — only applied on submit
  const [startYear, setStartYear] = React.useState('');
  const [startMonth, setStartMonth] = React.useState('');
  const [endYear, setEndYear] = React.useState('');
  const [endMonth, setEndMonth] = React.useState('');
  const [selectedLabels, setSelectedLabels] = React.useState([]);
  const [creatorStatus, setCreatorStatus] = React.useState('');

  // Applied filter state — triggers actual fetch; {} means "no filters applied"
  const [appliedFilters, setAppliedFilters] = React.useState({});

  // YT Audits toggle: true = admin audits (default), false = other audits
  const [ytAuditIsAdmin, setYtAuditIsAdmin] = React.useState(true);

  // Initial fetch + filter options on mount
  React.useEffect(() => {
    fetchFilterOptions();
    fetchAnalytics({});
    // eslint-disable-next-line camelcase
    fetchYtAudits({ is_admin: 'true' });
    return () => {
      clearAnalyticsData();
    };
  }, []);

  // Re-fetch when filters are submitted (skip the initial empty-object mount value)
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchAnalytics(appliedFilters);
    // eslint-disable-next-line camelcase
    fetchYtAudits({ ...appliedFilters, is_admin: ytAuditIsAdmin ? 'true' : 'false' });
  }, [appliedFilters]);

  // Re-fetch YT audits when toggle changes
  React.useEffect(() => {
    // eslint-disable-next-line camelcase
    fetchYtAudits({ ...appliedFilters, is_admin: ytAuditIsAdmin ? 'true' : 'false' });
  }, [ytAuditIsAdmin]);

  const handleSubmit = React.useCallback(() => {
    const p = {};
    // eslint-disable-next-line camelcase
    if (startYear && startMonth) p.start_date = `${startYear}-${startMonth}`;
    // eslint-disable-next-line camelcase
    else if (startYear) p.start_date = `${startYear}-01`;
    // eslint-disable-next-line camelcase
    if (endYear && endMonth) p.end_date = `${endYear}-${endMonth}`;
    // eslint-disable-next-line camelcase
    else if (endYear) p.end_date = `${endYear}-12`;
    // eslint-disable-next-line camelcase
    if (selectedLabels.length > 0) p.asset_labels = selectedLabels;
    // eslint-disable-next-line camelcase
    if (creatorStatus) p.creator_status = creatorStatus;
    setAppliedFilters(p);
  }, [startYear, startMonth, endYear, endMonth, selectedLabels, creatorStatus]);

  const handleReset = React.useCallback(() => {
    setStartYear('');
    setStartMonth('');
    setEndYear('');
    setEndMonth('');
    setSelectedLabels([]);
    setCreatorStatus('');
    setAppliedFilters({});
  }, []);

  const assetLabelOptions = React.useMemo(() => {
    if (!filterOptions) return [];
    return filterOptions.map((opt) => ({
      value: opt.assetLabel,
      display: opt.assetLabel,
    }));
  }, [filterOptions]);

  const hasFilters = !!(startYear || endYear || selectedLabels.length > 0 || creatorStatus);

  const revenueTotals = React.useMemo(() => {
    if (!revenueByMonth || revenueByMonth.length === 0) return null;
    return revenueByMonth.reduce(
      (acc, row) => ({
        totalPartnerRevenue:
          acc.totalPartnerRevenue + (row.totalPartnerRevenue || 0),
        redflagShare: acc.redflagShare + (row.redflagShare || 0),
        payableRevenue: acc.payableRevenue + (row.payableRevenue || 0),
      }),
      { totalPartnerRevenue: 0, redflagShare: 0, payableRevenue: 0 },
    );
  }, [revenueByMonth]);

  const estTotals = React.useMemo(() => {
    const byMonth = estimatedRevenue?.byMonth;
    if (!byMonth || byMonth.length === 0) return null;
    return byMonth.reduce(
      (acc, row) => ({
        totalEstimatedRevenue:
          acc.totalEstimatedRevenue + (row.totalEstimatedRevenue || 0),
        estimatedRedflagShare:
          acc.estimatedRedflagShare + (row.estimatedRedflagShare || 0),
        estimatedPayableRevenue:
          acc.estimatedPayableRevenue + (row.estimatedPayableRevenue || 0),
      }),
      {
        totalEstimatedRevenue: 0,
        estimatedRedflagShare: 0,
        estimatedPayableRevenue: 0,
      },
    );
  }, [estimatedRevenue]);

  const estByMonth = estimatedRevenue?.byMonth ?? null;
  const estByCreator = estimatedRevenue?.byCreator ?? null;

  const handleTabChange = React.useCallback((tab) => setActiveTab(tab), []);

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{ title: 'Creator Analytics', subTitle: 'Content ID' }}
    >
      <Seo title="Creator Analytics" />
      <Box
        pad={{
          horizontal: small ? '0.75rem' : '2rem',
          vertical: small ? '1rem' : '2rem',
        }}
        gap="1rem"
      >
        {/* ── Filters ── */}
        <StyledPanel>
          <Box gap="1rem">
            <Box direction="row" align="center" justify="between">
              <Text size="0.75rem" weight={700} color={page.secondaryText} style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Filters
              </Text>
              {hasFilters && (
                <AppButton
                  plain
                  overrideHover
                  level="bare"
                  label="Reset all"
                  color={page.highlight}
                  textSize="0.78rem"
                  onClick={handleReset}
                />
              )}
            </Box>

            <Box
              direction={small ? 'column' : 'row'}
              align={small ? 'stretch' : 'end'}
              gap={small ? '0.75rem' : '0.75rem'}
              wrap
            >
              {/* Date range group */}
              <Box direction="column" gap="0.35rem">
                <Text size="0.72rem" weight={600} color={page.secondaryText} style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Date Range
                </Text>
                <Box direction="row" align="center" gap="0.35rem">
                  <FlexibleSelectDropdown
                    flexibleOptions={YEAR_OPTIONS}
                    selectedOption={startYear}
                    handleItemSelect={setStartYear}
                    noSelectionLabel="From year"
                    displayKey="display"
                    valueKey="api"
                    textColor={page.primaryText}
                    buttonWidth="100px"
                  />
                  <FlexibleSelectDropdown
                    flexibleOptions={MONTH_OPTIONS}
                    selectedOption={startMonth}
                    handleItemSelect={setStartMonth}
                    noSelectionLabel="Month"
                    displayKey="display"
                    valueKey="api"
                    textColor={page.primaryText}
                    buttonWidth="120px"
                  />
                  <Text size="0.8rem" color={page.secondaryText} style={{ padding: '0 0.15rem' }}>→</Text>
                  <FlexibleSelectDropdown
                    flexibleOptions={YEAR_OPTIONS}
                    selectedOption={endYear}
                    handleItemSelect={setEndYear}
                    noSelectionLabel="To year"
                    displayKey="display"
                    valueKey="api"
                    textColor={page.primaryText}
                    buttonWidth="100px"
                  />
                  <FlexibleSelectDropdown
                    flexibleOptions={MONTH_OPTIONS}
                    selectedOption={endMonth}
                    handleItemSelect={setEndMonth}
                    noSelectionLabel="Month"
                    displayKey="display"
                    valueKey="api"
                    textColor={page.primaryText}
                    buttonWidth="120px"
                  />
                </Box>
              </Box>

              {/* Divider */}
              {!small && (
                <Box
                  height="36px"
                  width="1px"
                  background={page.tableBorder}
                  style={{ flexShrink: 0, alignSelf: 'flex-end', marginBottom: '1px' }}
                />
              )}

              {/* Creator multi-select */}
              {assetLabelOptions.length > 0 && (
                <Box direction="column" gap="0.35rem">
                  <Text size="0.72rem" weight={600} color={page.secondaryText} style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Creator
                  </Text>
                  <FlexibleSelectDropdown
                    flexibleOptions={assetLabelOptions}
                    selectedOption={selectedLabels}
                    handleItemSelect={setSelectedLabels}
                    noSelectionLabel="All creators"
                    displayKey="display"
                    valueKey="value"
                    textColor={page.primaryText}
                    buttonWidth="175px"
                    multiSelect
                    withSearch={assetLabelOptions.length > 8}
                  />
                </Box>
              )}

              {/* Status filter */}
              <Box direction="column" gap="0.35rem">
                <Text size="0.72rem" weight={600} color={page.secondaryText} style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Status
                </Text>
                <FlexibleSelectDropdown
                  flexibleOptions={[
                    { api: 'all', display: 'All creators' },
                    { api: 'active', display: 'Active only' },
                    { api: 'inactive', display: 'Inactive only' },
                  ]}
                  selectedOption={creatorStatus}
                  handleItemSelect={(v) => setCreatorStatus(v || '')}
                  noSelectionLabel="All creators"
                  displayKey="display"
                  valueKey="api"
                  textColor={page.primaryText}
                  buttonWidth="140px"
                  withReset={!!creatorStatus && creatorStatus !== 'all'}
                />
              </Box>

              {/* YT Audits type toggle — only shown on that tab */}
              {activeTab === 'YT Audits' && (
                <Box direction="column" gap="0.35rem">
                  <Text size="0.72rem" weight={600} color={page.secondaryText} style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Audit type
                  </Text>
                  <Box direction="row" gap="0.25rem">
                    <AppButton
                      level="dynamicSmall"
                      textSize="0.8rem"
                      bgColor={ytAuditIsAdmin ? button.primaryBg : button.secondaryBg}
                      color={ytAuditIsAdmin ? button.primaryText : button.secondaryText}
                      label="Admin"
                      onClick={() => setYtAuditIsAdmin(true)}
                    />
                    <AppButton
                      level="dynamicSmall"
                      textSize="0.8rem"
                      bgColor={!ytAuditIsAdmin ? button.primaryBg : button.secondaryBg}
                      color={!ytAuditIsAdmin ? button.primaryText : button.secondaryText}
                      label="Other"
                      onClick={() => setYtAuditIsAdmin(false)}
                    />
                  </Box>
                </Box>
              )}

              {/* Apply button — pinned to end */}
              <Box direction="column" gap="0.35rem" style={{ marginLeft: small ? 0 : 'auto' }}>
                <Text size="0.72rem" color="transparent" style={{ userSelect: 'none' }}>‌</Text>
                <AppButton
                  level="dynamicSmall"
                  textSize="0.8rem"
                  bgColor={button.primaryBg}
                  color={button.primaryText}
                  label="Apply Filters"
                  onClick={handleSubmit}
                  style={{ whiteSpace: 'nowrap' }}
                />
              </Box>
            </Box>
          </Box>
        </StyledPanel>

        {/* ── Tabs ── */}
        <TabBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          page={page}
        />

        {activeTab === 'Overview' && (
          <CidAnalyticsOverviewTab
            overview={overview}
            revenueTotals={revenueTotals}
            revenueByMonth={revenueByMonth}
            revenueRanks={revenueRanks}
            revenuePerCreator={revenuePerCreator}
            videoStats={videoStats}
            creatorGrowth={creatorGrowth}
            loading={loading}
            page={page}
            small={small}
            hasFilters={hasFilters}
          />
        )}

        {activeTab === 'Weekly Trends' && (
          <CidAnalyticsWeeklyTab
            weeklyGrowth={weeklyGrowth}
            viewsAnalytics={viewsAnalytics}
            estimatedRevenue={estimatedRevenue}
            loading={loading}
            page={page}
            small={small}
            hasFilters={hasFilters}
          />
        )}

        {activeTab === 'Performance' && (
          <CidAnalyticsPerformanceTab
            viewsAnalytics={viewsAnalytics}
            claimsPerformance={claimsPerformance}
            loading={loading}
            page={page}
            small={small}
          />
        )}

        {activeTab === 'Estimated KPIs' && (
          <CidAnalyticsEstimatedTab
            estTotals={estTotals}
            estByMonth={estByMonth}
            estByCreator={estByCreator}
            loading={loading}
            page={page}
            small={small}
            hasFilters={hasFilters}
          />
        )}

        {activeTab === 'YT Audits' && (
          <CidAnalyticsYtAuditsTab
            ytAudits={ytAudits}
            loading={loading}
            page={page}
            small={small}
            isAdmin={ytAuditIsAdmin}
          />
        )}
      </Box>
    </AdminNavWrapper>
  );
};

AdminCidAnalyticsDashboardPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string.isRequired })
    .isRequired,
  loading: PropTypes.bool.isRequired,
  fetchAnalytics: PropTypes.func.isRequired,
  fetchYtAudits: PropTypes.func.isRequired,
  clearAnalyticsData: PropTypes.func.isRequired,
  fetchFilterOptions: PropTypes.func.isRequired,
  filterOptions: PropTypes.arrayOf(
    PropTypes.shape({
      assetLabel: PropTypes.string,
      companyId: PropTypes.number,
    }),
  ),
  overview: PropTypes.shape({
    totalCreators: PropTypes.number,
    totalOffboardedCreators: PropTypes.number,
    totalActiveClaims: PropTypes.number,
    totalReferenceVideos: PropTypes.number,
    totalEstimatedPartnerRevenue: PropTypes.number,
  }),
  creatorGrowth: PropTypes.array,
  revenueByMonth: PropTypes.array,
  revenueRanks: PropTypes.array,
  videoStats: PropTypes.array,
  weeklyGrowth: PropTypes.array,
  viewsAnalytics: PropTypes.shape({
    byWeek: PropTypes.array,
    byCreator: PropTypes.array,
    overallRpm: PropTypes.number,
    rpmByMonth: PropTypes.array,
    zeroViewPct: PropTypes.number,
    zeroViewAssetCount: PropTypes.number,
    totalActiveAssetCount: PropTypes.number,
  }),
  claimsPerformance: PropTypes.shape({
    lifetimeLow: PropTypes.shape({
      count: PropTypes.number,
      items: PropTypes.array,
    }),
    lifetimeHigh: PropTypes.shape({
      count: PropTypes.number,
      items: PropTypes.array,
    }),
    dailyLow: PropTypes.shape({
      count: PropTypes.number,
      items: PropTypes.array,
    }),
    dailyHigh: PropTypes.shape({
      count: PropTypes.number,
      items: PropTypes.array,
    }),
  }),
  revenuePerCreator: PropTypes.array,
  estimatedRevenue: PropTypes.shape({
    byMonth: PropTypes.array,
    byCreator: PropTypes.array,
  }),
  ytAudits: PropTypes.shape({
    overview: PropTypes.object,
    byMonth: PropTypes.array,
    statusBreakdown: PropTypes.object,
    audits: PropTypes.array,
  }),
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    overview: state.adminCidAnalytics.overview,
    creatorGrowth: state.adminCidAnalytics.creatorGrowth,
    revenueByMonth: state.adminCidAnalytics.revenueByMonth,
    revenueRanks: state.adminCidAnalytics.revenueRanks,
    videoStats: state.adminCidAnalytics.videoStats,
    weeklyGrowth: state.adminCidAnalytics.weeklyGrowth,
    viewsAnalytics: state.adminCidAnalytics.viewsAnalytics,
    claimsPerformance: state.adminCidAnalytics.claimsPerformance,
    revenuePerCreator: state.adminCidAnalytics.revenuePerCreator,
    estimatedRevenue: state.adminCidAnalytics.estimatedRevenue,
    filterOptions: state.adminCidAnalytics.filterOptions,
    ytAudits: state.adminCidAnalytics.ytAudits,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchAnalytics: initiateFetchCidAnalytics,
      fetchYtAudits: initiateFetchYtAudits,
      clearAnalyticsData: clearCidAnalytics,
      fetchFilterOptions: initiateFetchCidAnalyticsFilterOptions,
    },
    dispatch,
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AdminCidAnalyticsDashboardPage);
